import { useRef } from 'react';

/**
 * Retorna uma função com identidade ESTÁVEL entre renders, que sempre chama a versão mais
 * recente de `fn` (sem "closure obsoleta" — a referência interna é atualizada a cada render,
 * de forma síncrona, antes de qualquer chamador poder invocá-la).
 *
 * Por quê: funções expostas por Context Providers (ex.: `showSnackbar`, `login`, `refreshDemands`)
 * são recriadas a cada render se não forem memoizadas. Se algum componente colocar essa função
 * em um array de dependências de `useEffect`/`useCallback`, e a própria função disparar um
 * re-render do Provider (ex.: via setState), o efeito volta a disparar — podendo criar um loop
 * de re-renderizações/chamadas de API. Esse hook resolve isso na raiz, sem exigir reescrever a
 * lógica interna de cada função nem mapear manualmente suas dependências (arriscado em funções
 * complexas/interdependentes, como as de autenticação).
 */
export function useStableCallback<T extends (...args: any[]) => any>(fn: T): T {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  const stableRef = useRef<T | null>(null);
  if (!stableRef.current) {
    stableRef.current = ((...args: Parameters<T>) => fnRef.current(...args)) as T;
  }
  return stableRef.current;
}
